import { Controller } from '@hotwired/stimulus';

export default class SessionTimeoutController extends Controller {
  static targets = ['modal', 'countdown'];

  // Declare Stimulus targets
  declare readonly modalTarget: HTMLElement;
  declare readonly countdownTarget: HTMLElement;

  // 15 minutes in milliseconds
  private timeoutDuration = 15 * 60 * 1000;
  // 2 minutes warning before timeout
  private warningDuration = 2 * 60 * 1000;
  private activityTimer: number | null = null;
  private warningTimer: number | null = null;
  private countdownInterval: number | null = null;
  private lastActivity: number = Date.now();
  private modalVisible = false;
  private boundHandleActivity!: () => void;

  connect() {
    // Initialize the bound handler before setting up listeners
    this.boundHandleActivity = this.handleActivity.bind(this);
    this.resetTimers();
    this.setupActivityListeners();
  }

  disconnect() {
    this.clearAllTimers();
    this.removeActivityListeners();
  }

  // Action called when user clicks "Stay Logged In"
  stayLoggedIn() {
    this.hideWarningModal();
    this.keepAlive();
    this.resetTimers();
  }

  // Action called when user clicks "Log Out"
  logoutNow() {
    this.logoutUser();
  }

  private setupActivityListeners() {
    // Track user activity
    document.addEventListener('mousemove', this.boundHandleActivity);
    document.addEventListener('keypress', this.boundHandleActivity);
    document.addEventListener('click', this.boundHandleActivity);
    document.addEventListener('scroll', this.boundHandleActivity);
    document.addEventListener('touchstart', this.boundHandleActivity);
  }

  private removeActivityListeners() {
    document.removeEventListener('mousemove', this.boundHandleActivity);
    document.removeEventListener('keypress', this.boundHandleActivity);
    document.removeEventListener('click', this.boundHandleActivity);
    document.removeEventListener('scroll', this.boundHandleActivity);
    document.removeEventListener('touchstart', this.boundHandleActivity);
  }

  private handleActivity() {
    // Don't reset timers if modal is visible - let the countdown continue
    if (this.modalVisible) {
      return;
    }

    const now = Date.now();
    // Only reset if more than 1 second has passed since last activity (debounce)
    if (now - this.lastActivity > 1000) {
      this.lastActivity = now;
      this.resetTimers();
    }
  }

  private resetTimers() {
    this.clearAllTimers();

    // Set warning timer (13 minutes)
    this.warningTimer = window.setTimeout(() => {
      this.showWarningModal();
    }, this.timeoutDuration - this.warningDuration);

    // Set logout timer (15 minutes)
    this.activityTimer = window.setTimeout(() => {
      // Only logout if modal is visible (meaning warning was shown)
      // This prevents early logout if timer gets confused
      this.logoutUser();
    }, this.timeoutDuration);
  }

  private clearAllTimers() {
    if (this.activityTimer) {
      clearTimeout(this.activityTimer);
      this.activityTimer = null;
    }
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  private showWarningModal() {
    if (this.hasModalTarget) {
      this.modalVisible = true;
      this.modalTarget.classList.remove('session-timeout-hidden');
      this.modalTarget.classList.add('session-timeout-visible');
      this.startCountdown();
    }
  }

  private hideWarningModal() {
    if (this.hasModalTarget) {
      this.modalVisible = false;
      this.modalTarget.classList.remove('session-timeout-visible');
      this.modalTarget.classList.add('session-timeout-hidden');
    }
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  private startCountdown() {
    let remainingSeconds = Math.floor(this.warningDuration / 1000);

    const updateCountdown = () => {
      if (this.hasCountdownTarget) {
        const minutes = Math.floor(remainingSeconds / 60);
        const seconds = remainingSeconds % 60;
        this.countdownTarget.textContent = `${minutes}:${seconds.toString().padStart(2, '0')}`;
      }

      remainingSeconds--;

      if (remainingSeconds < 0) {
        if (this.countdownInterval) {
          clearInterval(this.countdownInterval);
          this.countdownInterval = null;
        }
        // Ensure logout happens even if the main timer failed
        if (this.modalVisible) {
          this.logoutUser();
        }
      }
    };

    // Update immediately
    updateCountdown();
    this.countdownInterval = window.setInterval(updateCountdown, 1000);
  }

  private async keepAlive() {
    const csrfToken = this.getCSRFToken();
    if (!csrfToken) {
      // eslint-disable-next-line no-console
      console.error('Cannot send keep-alive request: CSRF token not found');
      return;
    }

    try {
      const response = await fetch('/users/keep_alive', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json', // eslint-disable-line @typescript-eslint/naming-convention
          'X-CSRF-Token': csrfToken, // eslint-disable-line @typescript-eslint/naming-convention
          Accept: 'application/json',
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        // eslint-disable-next-line no-console
        console.error('Failed to keep session alive');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error keeping session alive:', error);
    }
  }

  private async logoutUser() {
    // Clear all timers before logout
    this.clearAllTimers();

    // Hide modal if visible
    if (this.modalVisible) {
      this.hideWarningModal();
    }

    const csrfToken = this.getCSRFToken();
    if (!csrfToken) {
      // eslint-disable-next-line no-console
      console.error('Cannot send timeout request: CSRF token not found');
      // Still redirect to sign in page
      window.location.href = '/users/sign_in';
      return;
    }

    // Make a request to the timeout endpoint to trigger proper Devise timeout
    try {
      const response = await fetch('/users/timeout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json', // eslint-disable-line @typescript-eslint/naming-convention
          'X-CSRF-Token': csrfToken, // eslint-disable-line @typescript-eslint/naming-convention
          Accept: 'application/json',
        },
        credentials: 'same-origin',
      });

      if (response.ok) {
        const data = await response.json();
        // Small delay to ensure session is properly cleared
        setTimeout(() => {
          window.location.href = data.redirect_url || '/users/sign_in';
        }, 100);
      } else {
        // Fallback: redirect to sign in page
        window.location.href = '/users/sign_in';
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error during timeout logout:', error);
      // Fallback: redirect to sign in page
      window.location.href = '/users/sign_in';
    }
  }

  private getCSRFToken(): string | null {
    const token = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement;
    if (!token) {
      // eslint-disable-next-line no-console
      console.warn('CSRF token meta tag not found. Requests may be unprotected.');
      return null;
    }
    return token.content;
  }

  // Getters for targets
  private get hasModalTarget(): boolean {
    return this.modalTarget !== undefined;
  }

  private get hasCountdownTarget(): boolean {
    return this.countdownTarget !== undefined;
  }

}
